import { useState } from 'react';
import { strings } from '@/data/strings';
import { AnimatedBlueBackdrop } from '@/components/AnimatedBlueBackdrop/AnimatedBlueBackdrop';
import { useInView } from '@/hooks/useInView';
import styles from './FinalCTA.module.css';

function cx(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(' ');
}

// Link a App Store: la app ya está publicada (id6782409624).
// Android todavía NO está en Play Store, así que el botón de Google no linkea a
// la store (evitamos mandar a un "app no encontrada"): al tocarlo revela un
// aviso de "próximamente". Cuando exista el listing, volver a un <a> con href.
const APP_STORE_ID = 'id6782409624';
const APP_STORE_HREF = APP_STORE_ID
  ? `https://apps.apple.com/ar/app/profind/${APP_STORE_ID}`
  : '';
const PROFESSIONALS_HREF = '#para-profesionales';

export function FinalCTA() {
  const { ref, inView } = useInView<HTMLDivElement>({
    threshold: 0,
    rootMargin: '0px 0px -15% 0px',
  });
  const [showAndroidNote, setShowAndroidNote] = useState(false);

  return (
    <section id="descargar" className={styles.section}>
      <AnimatedBlueBackdrop />

      <div
        ref={ref}
        className={cx('container', styles.inner, inView && styles.revealed)}
      >
        <h2 className={styles.title}>{strings.finalCta.title}</h2>
        <p className={styles.subtitle}>{strings.finalCta.subtitle}</p>

        <div
          className={cx(styles.stores, inView && styles.storesPulse)}
          role="group"
          aria-label={strings.finalCta.title}
        >
          {APP_STORE_HREF && (
            <a
              className={cx(styles.store, styles.storeAvailable)}
              href={APP_STORE_HREF}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={strings.finalCta.storeAppleAria}
            >
              <span className={styles.storeIcon} aria-hidden="true">
                <IconApple />
              </span>
              <span className={styles.storeText}>
                <span className={styles.storeKicker}>
                  {strings.finalCta.storeKicker}
                </span>
                <span className={styles.storeName}>
                  {strings.finalCta.storeApple}
                </span>
              </span>
            </a>
          )}

          <button
            type="button"
            className={cx(styles.store, styles.comingSoon)}
            aria-label={strings.finalCta.storeGoogleAria}
            aria-pressed={showAndroidNote}
            onClick={() => setShowAndroidNote((v) => !v)}
          >
            <span className={styles.storeIcon} aria-hidden="true">
              <IconGooglePlay />
            </span>
            <span className={styles.storeText}>
              <span className={styles.storeKicker}>
                {strings.finalCta.storeGoogleKicker}
              </span>
              <span className={styles.storeName}>
                {strings.finalCta.storeGoogle}
              </span>
            </span>
            <span className={styles.badge} aria-hidden="true">
              {strings.finalCta.storeGoogleBadge}
            </span>
          </button>
        </div>

        <p className={styles.androidNote} role="status" aria-live="polite">
          {showAndroidNote && (
            <span className={styles.androidNoteInner}>
              {strings.finalCta.storeGoogleNote}
            </span>
          )}
        </p>

        <a className={styles.secondary} href={PROFESSIONALS_HREF}>
          <span>{strings.finalCta.secondary}</span>
          <span className={styles.secondaryArrow} aria-hidden="true">→</span>
        </a>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Icons
// ─────────────────────────────────────────────────────────────────────────────

function IconApple() {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M16.365 1.43c0 1.14-.42 2.21-1.17 3.03-.81.9-2.14 1.6-3.27 1.51-.13-1.12.42-2.27 1.16-3.06.85-.9 2.27-1.6 3.28-1.48ZM20.5 17.36c-.55 1.27-.82 1.84-1.53 2.97-.99 1.58-2.39 3.55-4.13 3.57-1.54.01-1.94-1-4.04-1-2.1.01-2.54 1.02-4.08 1-1.74-.02-3.07-1.8-4.06-3.38C.32 16.91-.27 11.7 2.43 8.95c1.06-1.08 2.5-1.77 4.05-1.79 1.57-.02 3.05 1.06 4.04 1.06.99 0 2.79-1.31 4.7-1.12.8.03 3.04.32 4.48 2.43-.12.08-2.68 1.56-2.65 4.66.03 3.7 3.21 4.93 3.25 4.95l-.8 2.22Z" />
    </svg>
  );
}

function IconGooglePlay() {
  return (
    <svg
      width="26"
      height="28"
      viewBox="0 0 24 26"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M3.6 1.4C3.2 1.6 3 2 3 2.6v20.8c0 .6.2 1 .6 1.2L15 13.5 3.6 1.4Z"
        fill="currentColor"
        opacity="0.95"
      />
      <path
        d="M15 13.5 18.7 9.8 5.5 1.1C5 .8 4.4.9 4 1.1L15 13.5Z"
        fill="currentColor"
        opacity="0.7"
      />
      <path
        d="m15 13.5 3.7 3.7 3-1.7c.9-.5.9-1.8 0-2.3L18.7 9.8 15 13.5Z"
        fill="currentColor"
      />
      <path
        d="M4 25.9c.4.2 1 .3 1.5 0l13.2-8.7L15 13.5 4 25.9Z"
        fill="currentColor"
        opacity="0.85"
      />
    </svg>
  );
}
